'use client'

import { Collapsible, Icon, Span, Stack, Text } from '@chakra-ui/react'
import { useQuery } from '@rocicorp/zero/react'
import { Sidebar } from '@saas-ui/react'
import { createLink } from '@tanstack/react-router'

import { ChevronRightIcon } from '#components/icons/chevron-right-icon.tsx'
import { FolderIcon } from '#components/icons/folder-icon.tsx'
import { GitPullRequestIcon } from '#components/icons/git-pull-request-icon.tsx'
import { Shimmer } from '#components/shimmer.tsx'
import { ProgressIcon } from '#components/ui/progress-icon.tsx'
import { useCurrentWorkspace } from '#features/common/hooks/use-current-workspace'
import { getStepProgress } from '#features/implementations/components/implementation-status.tsx'
import { resolveTaskListViewState } from '#features/implementations/task-list-view-state.ts'
import {
  getImplementationDisplayInfo,
  isProgressingActivity,
  statusConfig,
} from '#features/implementations/utils/step-labels'
import { queries } from '#zero/queries.ts'
import {
  projectRunWorkflowState,
  type RunWithProjections,
} from '#zero/run-projection-from-zero.ts'

import type {
  Brief,
  RunStep,
  Implementation as ZeroImplementation,
} from '../../../../zero-schema.gen.ts'

export { getImplementationDisplayInfo }

type SidebarImplementationRow = ZeroImplementation & {
  brief?: Brief | null
  currentRun?: RunWithProjections | null
  steps: ReadonlyArray<RunStep>
}

function toSidebarImplementation(implementation: SidebarImplementationRow) {
  const currentRun = implementation.currentRun
  const primaryRepo = currentRun?.runRepos?.[0]
  const status = currentRun?.status ?? 'pending'
  const projection = projectRunWorkflowState(currentRun, implementation.steps)

  return {
    ...implementation,
    brief: implementation.brief ?? null,
    status,
    sandboxId: currentRun?.sandboxId ?? null,
    sessionId: currentRun?.sessionId ?? null,
    repoUrl: primaryRepo?.repoUrl ?? null,
    branch: primaryRepo?.branch ?? null,
    baseBranch: primaryRepo?.baseBranch ?? null,
    branchPushed: primaryRepo?.branchPushed ?? false,
    prUrl: primaryRepo?.prUrl ?? null,
    prStatus: primaryRepo?.prStatus ?? null,
    timelineMessages: currentRun?.timelineMessages ?? null,
    streamFormat: currentRun?.streamFormat ?? 'text',
    error: currentRun?.error ?? null,
    plan: currentRun?.plan ?? null,
    analysis: currentRun?.analysis ?? null,
    agentSessionId: currentRun?.agentSessionId ?? null,
    workflowId: currentRun?.workflowId ?? null,
    workflowVersion: currentRun?.workflowVersion ?? null,
    currentStepId: currentRun?.currentStepId ?? null,
    currentStepName: currentRun?.currentStepName ?? null,
    usageStats: currentRun?.usageStats ?? null,
    ...projection,
    isRunning: status === 'running',
  }
}

type Implementation = ReturnType<typeof toSidebarImplementation>

/**
 * Extracts "owner/repo" from a GitHub URL, stripping any trailing `.git`.
 * Returns "Other" for null/empty/unparseable URLs.
 */
export function getRepoName(repoUrl: string | null): string {
  if (!repoUrl) return 'Other'
  const segments = repoUrl
    .replace(/\.git$/, '')
    .split('/')
    .filter(Boolean)
  if (segments.length < 2) return 'Other'
  return `${segments[segments.length - 2]}/${segments[segments.length - 1]}`
}

/**
 * Groups implementations by their repo name, preserving original order.
 * Returns an array of [repoName, implementations[]] tuples in first-seen order.
 */
export function groupImplementationsByRepo(
  implementations: ReadonlyArray<Implementation>,
): [string, Implementation[]][] {
  const groups = new Map<string, Implementation[]>()
  for (const impl of implementations) {
    const repo = getRepoName(impl.repoUrl ?? null)
    const group = groups.get(repo)
    if (group) {
      group.push(impl)
    } else {
      groups.set(repo, [impl])
    }
  }
  return Array.from(groups.entries())
}

/**
 * Returns active implementation items for the sidebar.
 * These are rendered as nested items under the "Implementations" nav link.
 */
export function SidebarImplementations() {
  const [workspace] = useCurrentWorkspace()

  const [rows, result] = useQuery(
    queries.implementations.listActive({ workspaceId: workspace.id }),
  )
  const implementations = rows.map(toSidebarImplementation)
  const isLoading = result.type === 'unknown'

  const viewState = resolveTaskListViewState({
    isLoading,
    itemCount: implementations.length,
  })

  if (viewState !== 'list') {
    return null
  }

  const groups = groupImplementationsByRepo(implementations)

  return (
    <>
      {groups.map(([repoName, impls]) => (
        <Collapsible.Root key={repoName} defaultOpen>
          <Collapsible.Trigger asChild>
            <Sidebar.NavItem role="group">
              <Sidebar.NavButton>
                <Icon as={FolderIcon} />
                {repoName.split('/')[1]}
                <Sidebar.NavButtonEndElement>
                  <Icon
                    as={ChevronRightIcon}
                    transition="transform 0.15s ease"
                    _groupOpen={{ transform: 'rotate(90deg)' }}
                  />
                </Sidebar.NavButtonEndElement>
              </Sidebar.NavButton>
            </Sidebar.NavItem>
          </Collapsible.Trigger>
          <Collapsible.Content>
            {impls.map((impl: Implementation) => (
              <ImplementationLink
                key={impl.id}
                implementation={impl}
                workspace={workspace.slug}
              />
            ))}
          </Collapsible.Content>
        </Collapsible.Root>
      ))}
    </>
  )
}

interface ImplementationLinkProps {
  implementation: Implementation
  workspace: string
}

function getPrNumber(prUrl: string | null): number | null {
  if (!prUrl) return null
  const match = prUrl.match(/\/pull\/(\d+)/)
  return match ? Number(match[1]) : null
}

function ImplementationLink({
  implementation,
  workspace,
}: ImplementationLinkProps) {
  const { title, label } = getImplementationDisplayInfo(implementation)

  const { status } = implementation
  const isRunning = isProgressingActivity(implementation.activity.key)
  const isIdle = status === 'pending' || status === 'queued'
  const hasPR = implementation.prStatus === 'open'
  const prNumber = getPrNumber(implementation.prUrl ?? null)
  const colorPalette =
    statusConfig[implementation.activity.key]?.colorPalette ??
    statusConfig[isRunning ? 'running' : status]?.colorPalette ??
    'gray'

  const progress = getStepProgress(implementation.steps)

  return (
    <Sidebar.NavItem>
      <NavLink
        to="/$workspace/tasks/$id"
        params={{
          workspace,
          id: implementation.id,
        }}
        activeProps={{ 'data-active': true }}
        alignItems="center"
      >
        <ProgressIcon
          size="1.1em"
          loading={isRunning}
          value={isIdle ? 0 : progress}
          colorPalette={colorPalette}
        />
        {isRunning && label ? (
          <Span fontSize="xs" pointerEvents="none" asChild>
            <Shimmer as="span">{label}</Shimmer>
          </Span>
        ) : null}

        <Span fontSize="xs" lineClamp={1} flex="1">
          {title}
        </Span>

        {hasPR && (
          <Stack direction="row" gap="0.5" alignItems="center" flexShrink={0}>
            <Icon as={GitPullRequestIcon} boxSize="3" color="fg.muted" />
            {prNumber && (
              <Text fontSize="xs" color="fg.muted">
                #{prNumber}
              </Text>
            )}
          </Stack>
        )}
      </NavLink>
    </Sidebar.NavItem>
  )
}

const NavLink = createLink(Sidebar.NavButton)
